import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumPalletStatus } from "./enums/enumPalletStatus.generated";
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class PalletHeaderReadOnly extends ModelObject<PalletHeaderReadOnly> {

    /**
    * The barcode generated for this pallet. Generated from sequence not user editable.
    * Not Nullable
    * Max Length : 1000
    */
    public barcode: string = "";
    private _deliveredToOrderId: Guid | null = null;
    /**
    * The order for which this pallet was delivered, if any. If this pallet was registered as delivered to multiple orders then last processed registration wins.
    */
    public get deliveredToOrderId(): Guid | null { return this._deliveredToOrderId }
    public set deliveredToOrderId(value: Guid | null) {
      this._deliveredToOrderId = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the pallet was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * User who created the pallet. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    private _disassembledAt: Instant | null = null;
    /**
    * Timestamp at which the pallet was disassembled on the system. Captured by system not user editable.
    */
    public get disassembledAt(): Instant | null { return this._disassembledAt }
    public set disassembledAt(value: Instant | null) {
      this._disassembledAt = value === undefined ? null : value;
    }
    private _disassembledByUserId: Guid | null = null;
    /**
    * User who disassembled the pallet. Captured by system not user editable.
    */
    public get disassembledByUserId(): Guid | null { return this._disassembledByUserId }
    public set disassembledByUserId(value: Guid | null) {
      this._disassembledByUserId = value === undefined ? null : value;
    }
    private _deliveredAt: Instant | null = null;
    /**
    * Timestamp at which the pallet was marked as delivered on the system. Captured by system not user editable. Will be the system timestamp at which the pallet delivered instruction was processed, and in  particular is NOT the time captured by the distrubtion app.
    */
    public get deliveredAt(): Instant | null { return this._deliveredAt }
    public set deliveredAt(value: Instant | null) {
      this._deliveredAt = value === undefined ? null : value;
    }
    private _deliveredByUserId: Guid | null = null;
    /**
    * User who delivered the pallet. Captured by system not user editable.
    */
    public get deliveredByUserId(): Guid | null { return this._deliveredByUserId }
    public set deliveredByUserId(value: Guid | null) {
      this._deliveredByUserId = value === undefined ? null : value;
    }
    /**
    * Pallet number. Allocated from pallet numbering sequence.
    * Not Nullable
    * Max Length : 1000
    */
    public number: string = "";
    public status: EnumPalletStatus = EnumPalletStatus.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.barcode = jsonSerializedObject.barcode;
          this.deliveredToOrderId = jsonSerializedObject.deliveredToOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveredToOrderId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.disassembledAt = jsonSerializedObject.disassembledAt == null ? null : Instant.parse(jsonSerializedObject.disassembledAt);
          this.disassembledByUserId = jsonSerializedObject.disassembledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.disassembledByUserId);
          this.deliveredAt = jsonSerializedObject.deliveredAt == null ? null : Instant.parse(jsonSerializedObject.deliveredAt);
          this.deliveredByUserId = jsonSerializedObject.deliveredByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveredByUserId);
          this.number = jsonSerializedObject.number;
          this.status = EnumPalletStatus.getByValue(jsonSerializedObject.status, this.status);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.barcode = this.barcode;
        toRet.deliveredToOrderId = this.deliveredToOrderId == null ? null : this.deliveredToOrderId.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.disassembledAt = this.disassembledAt == null ? null : this.disassembledAt.toString();
        toRet.disassembledByUserId = this.disassembledByUserId == null ? null : this.disassembledByUserId.toString();
        toRet.deliveredAt = this.deliveredAt == null ? null : this.deliveredAt.toString();
        toRet.deliveredByUserId = this.deliveredByUserId == null ? null : this.deliveredByUserId.toString();
        toRet.number = this.number;
        toRet.status = this.status.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "barcode", rule: (v: any) => v == null || v.length <= 1000 || "Barcode has too many characters (1000)"},
            {field: "deliveredToOrderId", rule: (v: any) => v == null || !v.isEmpty() || "PalletHeaderReadOnly.DeliveredToOrderId cannot be empty"},
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "PalletHeaderReadOnly.CreatedByUserId cannot be empty"},
            {field: "disassembledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "PalletHeaderReadOnly.DisassembledByUserId cannot be empty"},
            {field: "deliveredByUserId", rule: (v: any) => v == null || !v.isEmpty() || "PalletHeaderReadOnly.DeliveredByUserId cannot be empty"},
            {field: "number", rule: (v: any) => v == null || v.length <= 1000 || "Number has too many characters (1000)"},
    ];
}


export class PalletHeader extends ModelObjectWithDirtyCheck<PalletHeader> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    /**
    * PalletTypeId.
    */
    public palletTypeId: Guid = Guid.createEmpty();
    /**
    * Weight of this pallet when empty. (Initially copied from pallet type but can be changed).
    * Not Negative
    */
    public emptyWeightKg: number = 0;
    private _intendedForOrderId: Guid | null = null;
    /**
    * The order (if any) which this pallet is ear-marked for.
    */
    public get intendedForOrderId(): Guid | null { return this._intendedForOrderId }
    public set intendedForOrderId(value: Guid | null) {
      this._intendedForOrderId = value === undefined ? null : value;
    }
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    public readOnly: PalletHeaderReadOnly = new PalletHeaderReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.palletTypeId = Guid.parseOrEmpty(jsonSerializedObject.palletTypeId);
          this.emptyWeightKg = jsonSerializedObject.emptyWeightKg;
          this.intendedForOrderId = jsonSerializedObject.intendedForOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.intendedForOrderId);
          this.notes = jsonSerializedObject.notes;
          this.readOnly = new PalletHeaderReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.palletTypeId = this.palletTypeId.toString();
        toRet.emptyWeightKg = this.emptyWeightKg;
        toRet.intendedForOrderId = this.intendedForOrderId == null ? null : this.intendedForOrderId.toString();
        toRet.notes = this.notes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "PalletHeader.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "palletTypeId", rule: (v: any) => !v.isEmpty() || "PalletHeader.PalletTypeId cannot be empty"},
            {field: "intendedForOrderId", rule: (v: any) => v == null || !v.isEmpty() || "PalletHeader.IntendedForOrderId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class PalletLine extends ModelObject<PalletLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * The item which is on the pallet in this line.
    */
    public itemId: Guid = Guid.createEmpty();
    /**
    * URN of the item which is on the pallet in this line.
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * The quantity of this item line. If a container id is given then this should be 1.
    * Greater Than : 0
    */
    public quantity: number = 0;
    /**
    * Unit of Measure Id. This should match the unit of measure of the stock item.
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    private _containerId: Guid | null = null;
    /**
    * The Container which is on the pallet in this line. If specified then Quantity should be 1.
    */
    public get containerId(): Guid | null { return this._containerId }
    public set containerId(value: Guid | null) {
      this._containerId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.urn = jsonSerializedObject.urn;
          this.quantity = jsonSerializedObject.quantity;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.containerId = jsonSerializedObject.containerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.containerId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.itemId = this.itemId.toString();
        toRet.urn = this.urn;
        toRet.quantity = this.quantity;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.containerId = this.containerId == null ? null : this.containerId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "PalletLine.Id cannot be empty"},
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "PalletLine.ItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "quantity", rule: (v: any) => v == null || v > 0 || "Quantity is not greater than minimum allowed value: 0"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "PalletLine.UnitOfMeasureId cannot be empty"},
            {field: "containerId", rule: (v: any) => v == null || !v.isEmpty() || "PalletLine.ContainerId cannot be empty"},
    ];
}


export class PalletCalculated extends ModelObject<PalletCalculated> {

    /**
    * Sum of line total weight and pallet's empty weight
    */
    public totalWeightKg: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.totalWeightKg = jsonSerializedObject.totalWeightKg;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.totalWeightKg = this.totalWeightKg;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Pallet extends ModelObjectWithDirtyCheck<Pallet> {

    public header: PalletHeader = new PalletHeader();
    public lines: (PalletLine)[] = [];
    public calculated: PalletCalculated = new PalletCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new PalletHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new PalletLine(v));
          this.calculated = new PalletCalculated(jsonSerializedObject.calculated);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PalletLine[]).reduce((acc: Array<string>, curr: PalletLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class PalletFilter extends ModelObject<PalletFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _statuses: readonly (EnumPalletStatus)[] | null = null;
    public get statuses(): readonly (EnumPalletStatus)[] | null { return this._statuses }
    public set statuses(value: readonly (EnumPalletStatus)[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    public intendedForOrderOrNot: EnumYesNoEither = EnumYesNoEither.either;
    private _intendedForOrderIds: readonly (Guid)[] | null = null;
    public get intendedForOrderIds(): readonly (Guid)[] | null { return this._intendedForOrderIds }
    public set intendedForOrderIds(value: readonly (Guid)[] | null) {
      this._intendedForOrderIds = value === undefined ? null : value;
    }
    private _deliveredToOrderIds: readonly (Guid)[] | null = null;
    public get deliveredToOrderIds(): readonly (Guid)[] | null { return this._deliveredToOrderIds }
    public set deliveredToOrderIds(value: readonly (Guid)[] | null) {
      this._deliveredToOrderIds = value === undefined ? null : value;
    }
    private _palletIds: readonly (Guid)[] | null = null;
    public get palletIds(): readonly (Guid)[] | null { return this._palletIds }
    public set palletIds(value: readonly (Guid)[] | null) {
      this._palletIds = value === undefined ? null : value;
    }
    private _palletBarcodes: readonly (string)[] | null = null;
    public get palletBarcodes(): readonly (string)[] | null { return this._palletBarcodes }
    public set palletBarcodes(value: readonly (string)[] | null) {
      this._palletBarcodes = value === undefined ? null : value;
    }
    private _deliveredFrom: Instant | null = null;
    /**
    * Minimum (inclusive) delivered at timestamp.
    */
    public get deliveredFrom(): Instant | null { return this._deliveredFrom }
    public set deliveredFrom(value: Instant | null) {
      this._deliveredFrom = value === undefined ? null : value;
    }
    private _deliveredBefore: Instant | null = null;
    /**
    * Maximum (exclusive) delivered at timestamp.
    */
    public get deliveredBefore(): Instant | null { return this._deliveredBefore }
    public set deliveredBefore(value: Instant | null) {
      this._deliveredBefore = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumPalletStatus.getByValue(v));
          this.intendedForOrderOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.intendedForOrderOrNot, this.intendedForOrderOrNot);
          this.intendedForOrderIds = jsonSerializedObject.intendedForOrderIds == null ? null : jsonSerializedObject.intendedForOrderIds.map((v: any) => Guid.parseOrEmpty(v));
          this.deliveredToOrderIds = jsonSerializedObject.deliveredToOrderIds == null ? null : jsonSerializedObject.deliveredToOrderIds.map((v: any) => Guid.parseOrEmpty(v));
          this.palletIds = jsonSerializedObject.palletIds == null ? null : jsonSerializedObject.palletIds.map((v: any) => Guid.parseOrEmpty(v));
          this.palletBarcodes = jsonSerializedObject.palletBarcodes == null ? null : jsonSerializedObject.palletBarcodes.map((v: any) => v);
          this.deliveredFrom = jsonSerializedObject.deliveredFrom == null ? null : Instant.parse(jsonSerializedObject.deliveredFrom);
          this.deliveredBefore = jsonSerializedObject.deliveredBefore == null ? null : Instant.parse(jsonSerializedObject.deliveredBefore);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.intendedForOrderOrNot = this.intendedForOrderOrNot.value;
        toRet.intendedForOrderIds = this.intendedForOrderIds == null ? null : this.intendedForOrderIds.map(v => v.toString());
        toRet.deliveredToOrderIds = this.deliveredToOrderIds == null ? null : this.deliveredToOrderIds.map(v => v.toString());
        toRet.palletIds = this.palletIds == null ? null : this.palletIds.map(v => v.toString());
        toRet.palletBarcodes = this.palletBarcodes == null ? null : this.palletBarcodes.map(v => v);
        toRet.deliveredFrom = this.deliveredFrom == null ? null : this.deliveredFrom.toString();
        toRet.deliveredBefore = this.deliveredBefore == null ? null : this.deliveredBefore.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredPalletsResult extends ModelObject<FilteredPalletsResult> {

    public matchingCount: number = 0;
    public pallets: (Pallet)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.pallets = jsonSerializedObject.pallets.map((v: any) => new Pallet(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.pallets = this.pallets.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "pallets", rule: (v: any) => !!v || "pallets is required"},
            {field: "pallets", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Pallet[]).reduce((acc: Array<string>, curr: Pallet) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PalletService extends ServiceBase {

  public async getPallet(palletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Pallet> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletId:palletId.toString(),
    };
    const extractor = (response:any) => new Pallet(response.pallet);;
    return this.makeJsonRequest("pallet/v1/getPallet", req, false, extractor, opts);
  };

  public async getPallets(palletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Pallet)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletIds:palletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.pallets.map((v: any) => new Pallet(v));;
    return this.makeJsonRequest("pallet/v1/getPallets", req, false, extractor, opts);
  };

  public async getPalletsByFilter(filter: PalletFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredPalletsResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredPalletsResult(response.results);;
    return this.makeJsonRequest("pallet/v1/getPalletsByFilter", req, false, extractor, opts);
  };

  public async savePallet(pallet: Pallet, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    pallet:pallet.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("pallet/v1/savePallet", req, true, extractor, opts);
  };

  public async savePallets(pallets: readonly Pallet[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    pallets:pallets.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("pallet/v1/savePallets", req, true, extractor, opts);
  };

  public async markPalletDelivered(palletId: Guid, orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletId:palletId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("pallet/v1/markPalletDelivered", req, true, extractor, opts);
  };

  public async markPalletDisassembled(palletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletId:palletId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("pallet/v1/markPalletDisassembled", req, true, extractor, opts);
  };

  public async generatePalletLabel(palletId: Guid, documentFormat: EnumDocumentFileType, documentTemplateId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletId:palletId.toString(),
    documentFormat:documentFormat.value,
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("pallet/v1/generatePalletLabel", req, false, extractor, opts);
  };

  public async generatePalletLabels(palletIds: readonly Guid[], documentFormat: EnumDocumentFileType, documentTemplateId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletIds:palletIds.map(v => v.toString()),
    documentFormat:documentFormat.value,
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("pallet/v1/generatePalletLabels", req, false, extractor, opts);
  };

  public async generatePalletManifest(palletId: Guid, documentFormat: EnumDocumentFileType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletId:palletId.toString(),
    documentFormat:documentFormat.value,
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("pallet/v1/generatePalletManifest", req, false, extractor, opts);
  };

  public async markPalletsDeliveredByPalletBarcodes(palletBarcodes: readonly string[], orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletBarcodes:palletBarcodes.map(v => v),
    orderId:orderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("pallet/v1/markPalletsDeliveredByPalletBarcodes", req, true, extractor, opts);
  };

  public async unmarkPalletsDelivered(palletBarcodes: readonly string[], orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletBarcodes:palletBarcodes.map(v => v),
    orderId:orderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("pallet/v1/unmarkPalletsDelivered", req, true, extractor, opts);
  };
}

export const palletService = new PalletService();